/**
 * Keep Shopify Agent chat above the sticky ATB / Shop Now bar.
 *
 * DOM (nested shadows):
 *   shopify-chat                         overlay, bottom:0 — do not pin
 *     #shadow-root
 *       shopify-agent-runtime            Shopify sets --offset:20px here
 *         #shadow-root
 *           .activator-nudge             closed Chat button
 *           .panel                       open chat modal
 *
 * Allowed pages only:
 *   - CMRP (`body.course-materials-page` without `.discover-view-flow`):
 *     offset only while the ATB bar is visible and holding items.
 *   - Follett Discover (`body.discover-view-flow`):
 *     offset on mobile at all times. Never on desktop.
 *
 * When the offset does not apply, every property we set is removed.
 */
(function follettShopifyChatAtbOffset() {
  if (typeof window.__follettShopifyChatAtbOffsetDispose === 'function') {
    window.__follettShopifyChatAtbOffsetDispose();
  }

  var GAP_PX = 12;
  var MOBILE_MQ = '(max-width: 968px)';
  var STYLE_ID = 'follett-atb-chat-offset';
  var CHAT_EVENTS = [
    'shopify-agent:open',
    'shopify-agent:close',
    'shopify-chat:open',
    'shopify-chat:close',
  ];
  var POLL_MS = 120;
  var POLL_FOR_MS = 2500;
  var INNER_SEL = '.activator-nudge, button.activator, .panel';

  var pollTimer = 0;
  var pollUntil = 0;
  var listeningResize = false;
  var eventsBound = false;
  var mqBound = false;
  var lastEventAt = 0;
  var mq = window.matchMedia ? window.matchMedia(MOBILE_MQ) : null;

  function log() {
    var args = ['%c[follett-chat-atb]', 'color:#0284c7;font-weight:700'];
    for (var i = 0; i < arguments.length; i++) args.push(arguments[i]);
    console.log.apply(console, args);
  }

  function isDiscoverPage() {
    return Boolean(document.body && document.body.classList.contains('discover-view-flow'));
  }

  function isCmrpPage() {
    var body = document.body;
    if (!body || !body.classList.contains('course-materials-page')) return false;
    if (body.classList.contains('discover-view-flow')) return false;
    return true;
  }

  function isAllowedPage() {
    return isCmrpPage() || isDiscoverPage();
  }

  function isMobile() {
    return Boolean(mq && mq.matches);
  }

  function chatEl() {
    return (
      document.querySelector('shopify-chat') ||
      document.getElementById('ShopifyChat')
    );
  }

  function barEl() {
    if (isDiscoverPage()) {
      return document.querySelector('selection-bar-component.selection-bar-discover');
    }
    if (isCmrpPage()) {
      return document.querySelector('selection-bar-component.selection-bar');
    }
    return null;
  }

  function selectedCount(bar) {
    if (!bar) return 0;
    var count = parseInt(bar.getAttribute('data-selected-count') || '0', 10);
    return Number.isFinite(count) ? count : 0;
  }

  function barIsShowing(bar) {
    if (!bar || !bar.classList.contains('selection-bar--visible')) return false;
    var style = window.getComputedStyle(bar);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    return bar.getBoundingClientRect().height > 0;
  }

  function visibleBar() {
    var bar = barEl();
    return barIsShowing(bar) ? bar : null;
  }

  function shouldRun() {
    if (!isAllowedPage() || !chatEl()) return false;
    if (isDiscoverPage()) return isMobile();
    var bar = visibleBar();
    return Boolean(bar && selectedCount(bar) > 0);
  }

  function offsetPx() {
    if (!shouldRun()) return null;
    var bar = visibleBar();
    if (bar) {
      var height = Math.ceil(bar.getBoundingClientRect().height);
      if (height > 0) return height + GAP_PX;
    }
    if (isDiscoverPage()) return 88 + GAP_PX;
    return null;
  }

  function describeEl(el) {
    if (!el || !el.tagName) return String(el);
    var cls =
      el.className && typeof el.className === 'string'
        ? '.' + el.className.trim().split(/\s+/).slice(0, 3).join('.')
        : '';
    return el.tagName.toLowerCase() + cls;
  }

  function walk(node, fn) {
    if (!node) return;
    fn(node);
    var i;
    if (node.querySelectorAll) {
      var kids;
      try {
        kids = node.querySelectorAll('*');
      } catch (e) {
        kids = [];
      }
      for (i = 0; i < kids.length; i++) {
        fn(kids[i]);
        if (kids[i].shadowRoot) walk(kids[i].shadowRoot, fn);
      }
    }
    if (node.shadowRoot) walk(node.shadowRoot, fn);
  }

  function runtimeEls(chat) {
    var found = [];
    var seen = new Set();
    walk(chat, function (el) {
      if (!el || el.nodeType !== 1 || el === chat) return;
      var tag = (el.localName || '').toLowerCase();
      if (tag !== 'shopify-agent-runtime' && !(el.classList && el.classList.contains('agent-shell__runtime'))) {
        return;
      }
      if (seen.has(el)) return;
      seen.add(el);
      found.push(el);
    });
    return found;
  }

  function innerEls(runtime) {
    var root = runtime && runtime.shadowRoot;
    if (!root) return [];
    try {
      return Array.prototype.slice.call(root.querySelectorAll(INNER_SEL));
    } catch (e) {
      return [];
    }
  }

  function pinBottom(el, offset) {
    if (!el || !el.style) return;
    if (offset == null) {
      el.style.removeProperty('bottom');
      el.style.removeProperty('inset-block-end');
      return;
    }
    var value = offset + 'px';
    el.style.setProperty('bottom', value, 'important');
    el.style.setProperty('inset-block-end', value, 'important');
  }

  function clearElOffset(el) {
    if (!el || !el.style) return;
    el.style.removeProperty('--shopify-agent-activator-offset');
    el.style.removeProperty('bottom');
    el.style.removeProperty('inset-block-end');
  }

  function putStyle(root, css) {
    if (!root) return;
    var el = root.getElementById && root.getElementById(STYLE_ID);
    if (css == null) {
      if (el && el.parentNode) el.parentNode.removeChild(el);
      return;
    }
    if (!el) {
      el = document.createElement('style');
      el.id = STYLE_ID;
      root.appendChild(el);
    }
    el.textContent = css;
  }

  function shadowCss(offset) {
    var value = offset + 'px';
    var varRule =
      '--shopify-agent-activator-offset:' + value + ' !important;';
    var bottomRule =
      'bottom:' + value + ' !important;inset-block-end:' + value + ' !important;';
    return (
      ':host{' + varRule + '}' +
      INNER_SEL +
      '{' +
      bottomRule +
      '}'
    );
  }

  function hostShadowCss(offset) {
    var value = offset + 'px';
    return (
      'shopify-agent-runtime,.agent-shell__runtime{' +
      '--shopify-agent-activator-offset:' +
      value +
      ' !important;bottom:0 !important;inset-block-end:0 !important;' +
      '}' +
      'shopify-agent-runtime::part(activator),' +
      'shopify-agent-runtime::part(dialog){' +
      'bottom:' +
      value +
      ' !important;inset-block-end:' +
      value +
      ' !important;' +
      '}'
    );
  }

  function applyOffset(offset) {
    var chat = chatEl();
    if (!chat) return;
    var runtimes = runtimeEls(chat);
    var i;
    var j;

    if (offset == null) {
      clearElOffset(chat);
      putStyle(chat.shadowRoot, null);
      for (i = 0; i < runtimes.length; i++) {
        clearElOffset(runtimes[i]);
        var inners = innerEls(runtimes[i]);
        for (j = 0; j < inners.length; j++) pinBottom(inners[j], null);
        putStyle(runtimes[i].shadowRoot, null);
      }
      return;
    }

    var value = offset + 'px';
    chat.style.setProperty('--shopify-agent-activator-offset', value, 'important');
    chat.style.removeProperty('bottom');
    chat.style.removeProperty('inset-block-end');
    putStyle(chat.shadowRoot, hostShadowCss(offset));

    for (i = 0; i < runtimes.length; i++) {
      var runtime = runtimes[i];
      runtime.style.setProperty(
        '--shopify-agent-activator-offset',
        value,
        'important'
      );
      // Full-bleed overlay so .activator-nudge / .panel `bottom` is viewport-relative
      // (same after open/close). Shopify's default runtime bottom:20px stacked with
      // the inner offset on first paint.
      runtime.style.setProperty('bottom', '0px', 'important');
      runtime.style.setProperty('inset-block-end', '0px', 'important');
      putStyle(runtime.shadowRoot, shadowCss(offset));
      var nodes = innerEls(runtime);
      for (j = 0; j < nodes.length; j++) pinBottom(nodes[j], offset);
    }
  }

  function snapshot(chat) {
    var runtimes = runtimeEls(chat);
    var inners = [];
    for (var i = 0; i < runtimes.length; i++) {
      var nodes = innerEls(runtimes[i]);
      for (var j = 0; j < nodes.length; j++) {
        inners.push(
          describeEl(nodes[j]) +
            ' bottom=' +
            window.getComputedStyle(nodes[j]).bottom
        );
      }
    }
    return {
      wanted: offsetPx(),
      page: isDiscoverPage() ? 'discover' : isCmrpPage() ? 'cmrp' : 'other',
      mobile: isMobile(),
      runtimeVar: runtimes[0]
        ? runtimes[0].style.getPropertyValue('--shopify-agent-activator-offset')
        : null,
      inners: inners,
    };
  }

  function apply(reason) {
    var offset = offsetPx();
    applyOffset(offset);
    if (reason === 'event' || window.follettChatAtbDebug) {
      log(reason, snapshot(chatEl()));
    }
  }

  function stopPoll() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = 0;
    }
    pollUntil = 0;
  }

  function startPoll(reason) {
    if (!shouldRun()) return;
    stopPoll();
    apply(reason);
    pollUntil = Date.now() + POLL_FOR_MS;
    pollTimer = setInterval(function () {
      if (Date.now() > pollUntil || !shouldRun()) {
        stopPoll();
        if (!shouldRun()) applyOffset(null);
        return;
      }
      applyOffset(offsetPx());
    }, POLL_MS);
  }

  function onChatEvent() {
    if (!shouldRun()) return;
    var now = Date.now();
    if (now - lastEventAt < 200) return;
    lastEventAt = now;
    startPoll('event');
  }

  function onResize() {
    onBarChange();
  }

  function setResize(on) {
    if (on === listeningResize) return;
    listeningResize = on;
    if (on) window.addEventListener('resize', onResize, { passive: true });
    else window.removeEventListener('resize', onResize);
  }

  function onMqChange() {
    onBarChange();
  }

  function setMq(on) {
    if (!mq || on === mqBound) return;
    mqBound = on;
    if (on) {
      if (mq.addEventListener) mq.addEventListener('change', onMqChange);
      else if (mq.addListener) mq.addListener(onMqChange);
    } else if (mq.removeEventListener) {
      mq.removeEventListener('change', onMqChange);
    } else if (mq.removeListener) {
      mq.removeListener(onMqChange);
    }
  }

  function deactivate() {
    stopPoll();
    applyOffset(null);
    if (window.follettChatAtbDebug) log('clear-offset', snapshot(chatEl()));
  }

  function bindChatEvents() {
    if (eventsBound || !chatEl()) return;
    eventsBound = true;
    for (var i = 0; i < CHAT_EVENTS.length; i++) {
      window.addEventListener(CHAT_EVENTS[i], onChatEvent, true);
    }
  }

  function onBarChange() {
    if (!isAllowedPage()) {
      setResize(false);
      setMq(false);
      deactivate();
      return;
    }
    bindChatEvents();
    setResize(true);
    setMq(true);
    if (!shouldRun()) {
      deactivate();
      return;
    }
    startPoll('bar-change');
  }

  window.follettSyncShopifyChatAtbOffset = onBarChange;
  window.__follettShopifyChatAtbOffset = true;
  window.__follettShopifyChatAtbOffsetDispose = function () {
    if (eventsBound) {
      for (var i = 0; i < CHAT_EVENTS.length; i++) {
        window.removeEventListener(CHAT_EVENTS[i], onChatEvent, true);
      }
      eventsBound = false;
    }
    setResize(false);
    setMq(false);
    deactivate();
    window.__follettShopifyChatAtbOffset = false;
  };

  function start() {
    if (!isAllowedPage()) return;
    onBarChange();
    if (!window.customElements || !customElements.whenDefined) return;
    if (customElements.get('shopify-chat')) return;
    customElements.whenDefined('shopify-chat').then(onBarChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
